<template>
    <div>
        <ts-page-title
            title="Profit and Loss"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: 'Profit and Loss (v3)', active: true }
            ]"
        />

        <!-- ────────────────── FILTER PANEL ────────────────── -->
        <ts-panel>
            <ts-panel-wrapper class="tw-py-3">
                <div
                    class="tw-flex tw-justify-between tw-items-center tw-cursor-pointer tw-select-none"
                    @click="filtersOpen = !filtersOpen"
                >
                    <div class="tw-flex tw-items-center tw-space-x-3 tw-flex-1 tw-min-w-0">
                        <i class="fas tw-text-gray-400"
                           :class="filtersOpen ? 'fa-chevron-down' : 'fa-chevron-right'"
                           style="width: 12px"></i>
                        <b class="tw-text-gray-700 tw-whitespace-nowrap">Filter Criteria</b>
                        <div v-if="activeChips.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1 tw-min-w-0">
                            <span v-for="chip in activeChips" :key="chip.key"
                                  class="chip-pill" :class="chip.tone || 'chip-default'">
                                <i v-if="chip.icon" :class="'fas ' + chip.icon + ' tw-mr-1'"></i>
                                {{ chip.label }}
                            </span>
                        </div>
                    </div>
                    <span class="tw-text-sm tw-text-blue-600 tw-whitespace-nowrap tw-ml-3">
                        {{ filtersOpen ? 'Collapse' : 'Edit filters' }}
                    </span>
                </div>

                <div v-show="filtersOpen" class="tw-mt-3">
                    <!-- Row 1: Branch / Currency / Classified By -->
                    <div class="filter-grid">
                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">
                                Branch
                                <span class="tw-text-gray-400 tw-font-normal tw-normal-case">(all if empty)</span>
                            </label>
                            <ts-branch-filter
                                @filter="value => (model.branch_id = value)"
                                :isPlaceholder="true"
                            />
                        </div>

                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Currency <span class="text-danger">*</span></label>
                            <div class="pill-group">
                                <button v-for="c in currencies" :key="c.currency_id"
                                        type="button" class="pill"
                                        :class="{ 'pill-active': model.currency_id === c.currency_id }"
                                        @click="model.currency_id = c.currency_id">
                                    {{ c.currency_code }}
                                </button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 5">
                            <label class="filter-lbl">Classified By</label>
                            <div class="pill-group">
                                <button type="button" class="pill"
                                        :class="{ 'pill-active': model.classified_by === 'summary' }"
                                        @click="model.classified_by = 'summary'">
                                    Summary
                                </button>
                                <button type="button" class="pill"
                                        :class="{ 'pill-active': model.classified_by === 'branch' }"
                                        @click="model.classified_by = 'branch'">
                                    By Branch
                                </button>
                                <button type="button" class="pill"
                                        :class="{ 'pill-active': model.classified_by === 'summary-by-branch' }"
                                        @click="model.classified_by = 'summary-by-branch'">
                                    Summary by Branch
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Row 2: Date range + Category + Search -->
                    <div class="filter-grid tw-mt-3 tw-items-end">
                        <div class="filter-cell" style="grid-column: span 5">
                            <label class="filter-lbl">Date Range <span class="text-danger">*</span></label>
                            <div class="tw-flex tw-items-stretch tw-gap-2">
                                <div class="pill-group">
                                    <button v-for="p in datePresets" :key="p.value"
                                            type="button" class="pill"
                                            :class="{ 'pill-active': model.date_preset === p.value }"
                                            @click="model.date_preset = p.value">
                                        {{ p.label }}
                                    </button>
                                </div>
                                <div v-if="isCustomDate" class="tw-flex-1 tw-flex tw-gap-1">
                                    <DatePicker type="date" format="dd-MM-yyyy"
                                                   placeholder="From"
                                                   style="flex:1"
                                                   :value="model.date_from"
                                                   @on-change="model.date_from = $event" />
                                    <DatePicker type="date" format="dd-MM-yyyy"
                                                   placeholder="To"
                                                   style="flex:1"
                                                   :value="model.date_to"
                                                   @on-change="model.date_to = $event" />
                                </div>
                                <div v-else class="date-preview">{{ presetPreview }}</div>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 3">
                            <label class="filter-lbl">Category</label>
                            <div class="pill-group">
                                <button type="button" class="pill"
                                        :class="{ 'pill-active': !model.category }"
                                        @click="model.category = null">
                                    All
                                </button>
                                <button type="button" class="pill"
                                        :class="{ 'pill-active': model.category === 'revenues' }"
                                        @click="model.category = 'revenues'">
                                    Revenues
                                </button>
                                <button type="button" class="pill"
                                        :class="{ 'pill-active': model.category === 'expenses' }"
                                        @click="model.category = 'expenses'">
                                    Expenses
                                </button>
                            </div>
                        </div>

                        <div class="filter-cell" style="grid-column: span 4">
                            <label class="filter-lbl">Search</label>
                            <div class="search-group">
                                <div class="search-input-wrap">
                                    <input type="text" class="form-control search-input"
                                           v-model="searchQuery"
                                           placeholder="Account # / Account name / Branch"
                                           @keyup.enter="onSearch" />
                                    <button v-if="searchQuery" type="button" class="search-clear"
                                            title="Clear search" @click.prevent="onClearSearch">
                                        <i class="fas fa-times-circle"></i>
                                    </button>
                                </div>
                                <button type="button" class="search-btn" :disabled="loading"
                                        @click.prevent="onSearch">
                                    <i class="fas fa-circle-notch fa-spin" v-if="loading"></i>
                                    <i class="fas fa-search" v-else></i>
                                    <span class="tw-ml-1">Search</span>
                                </button>
                                <button type="button" class="reset-btn" @click.prevent="onReset"
                                        title="Reset all filters">
                                    <i class="fas fa-undo"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ts-panel-wrapper>
        </ts-panel>

        <!-- ────────────────── SUMMARY CARDS ────────────────── -->
        <div class="summary-grid tw-mt-3">
            <div v-for="card in summaryCards" :key="card.key"
                 class="summary-card" :class="'summary-card--' + card.tone">
                <div class="summary-card__header">
                    <div class="summary-card__icon"><i :class="'fas ' + card.icon"></i></div>
                    <div class="summary-card__label">{{ card.label }}</div>
                    <div v-if="card.badge !== undefined" class="summary-card__count">
                        <template v-if="loading">…</template>
                        <template v-else>{{ card.badge }}</template>
                    </div>
                </div>
                <div class="summary-card__value">
                    <template v-if="loading"><span class="tw-text-gray-300">—</span></template>
                    <template v-else>
                        <span class="summary-card__amount">{{ card.formatted }}</span>
                        <span v-if="card.unit" class="summary-card__currency">{{ card.unit }}</span>
                    </template>
                </div>
            </div>
        </div>

        <!-- ────────────────── RESULTS CARD ────────────────── -->
        <div class="results-card tw-mt-3">
            <div class="results-toolbar">
                <div class="tw-flex tw-items-center tw-gap-3 tw-text-sm tw-flex-wrap">
                    <template v-if="loading">
                        <span class="tw-text-gray-500"><i class="fas fa-circle-notch fa-spin tw-mr-1"></i> Loading…</span>
                    </template>
                    <template v-else-if="meta.total === null">
                        <span class="tw-text-gray-400"><i class="fas fa-info-circle tw-mr-1"></i> No search yet</span>
                    </template>
                    <template v-else-if="meta.total === 0">
                        <span class="tw-text-gray-500"><i class="far fa-folder-open tw-mr-1"></i> No results</span>
                    </template>
                    <template v-else>
                        <span class="tw-font-bold tw-text-gray-800 tw-text-base">
                            {{ meta.total.toLocaleString() }} {{ model.classified_by === 'branch' ? 'branches' : 'rows' }}
                        </span>
                        <span class="tw-text-gray-500">
                            · Page {{ meta.current_page }} of {{ meta.last_page }}
                        </span>
                    </template>
                </div>

                <div class="export-group" title="Export the current filters to xlsx">
                    <select v-model="model.excel_template" class="export-select">
                        <option value="standard">Standard xlsx</option>
                        <option value="raw-data">Raw Data xlsx</option>
                    </select>
                    <button type="button" class="export-btn" :disabled="exporting"
                            @click.prevent="exportExcel">
                        <i class="fas fa-circle-notch fa-spin" v-if="exporting"></i>
                        <i class="far fa-file-excel" v-else></i>
                        <span class="tw-ml-1">Export</span>
                    </button>
                    <button
                        type="button"
                        class="pdf-btn tw-ml-2"
                        :disabled="pdfLoading"
                        @click.prevent="printPdf"
                        title="Preview as PDF"
                    >
                        <i class="fas fa-circle-notch fa-spin" v-if="pdfLoading"></i>
                        <i class="far fa-file-pdf" v-else></i>
                        <span class="tw-ml-1">PDF</span>
                    </button>
                </div>
            </div>

            <!-- Full-screen PDF preview -->
            <Modal
                v-model="pdfModalOpen"
                fullscreen
                :closable="true"
                :mask-closable="false"
                :footer-hide="true"
                class-name="pdf-modal"
                @on-cancel="onClosePdfModal"
            >
                <div slot="header" class="tw-flex tw-items-center tw-justify-between">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <i class="far fa-file-pdf tw-text-red-600"></i>
                        <b>Profit and Loss — Preview</b>
                    </div>
                    <div class="tw-flex tw-items-center tw-gap-2 tw-mr-6">
                        <a v-if="pdfFileUrl" :href="pdfFileUrl" target="_blank" rel="noopener"
                           class="pdf-modal-action" title="Open in new tab">
                            <i class="fas fa-external-link-alt tw-mr-1"></i> Open
                        </a>
                        <a :href="pdfFileUrl || '#'"
                           :download="pdfFileUrl ? '' : null"
                           class="pdf-modal-action"
                           :class="{ 'tw-pointer-events-none tw-opacity-50': !pdfFileUrl }"
                           title="Download PDF">
                            <i class="fas fa-download tw-mr-1"></i> Download
                        </a>
                    </div>
                </div>
                <div class="pdf-modal-body">
                    <Spin size="large" fix v-if="pdfLoading"></Spin>
                    <iframe v-if="pdfFileUrl" :src="pdfFileUrl" frameborder="0"
                            class="pdf-frame" title="Profit and Loss PDF"></iframe>
                </div>
            </Modal>

            <div class="results-body tw-relative"
                 :style="{ minHeight: hasRows ? '0px' : emptyBodyHeight + 'px' }">
                <Spin size="large" fix v-if="loading"></Spin>

                <div v-if="!loading && meta.total === null" class="empty-state">
                    <i class="fas fa-search tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">
                        Pick a currency and date range, then click <b>Search</b>.
                    </div>
                </div>

                <div v-else-if="!loading && meta.total === 0" class="empty-state">
                    <i class="far fa-folder-open tw-text-2xl tw-mb-2 tw-text-gray-300"></i>
                    <div class="tw-text-sm tw-text-gray-500">
                        No profit-and-loss activity. Try widening the branch or date range.
                    </div>
                </div>

                <Table v-else
                         :columns="columns" :data="rows"
                         border stripe size="small"
                         :height="tableHeight"
                         show-summary
                         :summary-method="summaryMethod"
                         @on-sort-change="onSortChange">
                </Table>
            </div>

            <div v-if="meta.total > 0" class="results-pagination">
                <div class="pagination-left">
                    <span class="pagination-lbl">Per page:</span>
                    <Select v-model="perPage" @on-change="onPerPageChange"
                              style="width: 80px" size="small">
                        <Option :value="30">30</Option>
                        <Option :value="50">50</Option>
                        <Option :value="100">100</Option>
                    </Select>
                    <span class="pagination-divider">|</span>
                    <span class="pagination-lbl">
                        Showing <b>{{ rangeStart.toLocaleString() }}</b>–<b>{{ rangeEnd.toLocaleString() }}</b>
                        of <b>{{ meta.total.toLocaleString() }}</b>
                    </span>
                </div>
                <Page :total="meta.total" :current="meta.current_page" :page-size="perPage"
                        size="small" :show-elevator="meta.last_page > 10"
                        @on-change="onPageChange" />
            </div>
        </div>
    </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
import { trim } from 'lodash'
import moment from 'moment'
import * as FileDownload from 'downloadjs'

export default {
    name: 'profitAndLossV3',
    data () {
        return {
            filtersOpen: true,
            loading: false,
            exporting: false,
            pdfLoading: false,
            pdfModalOpen: false,
            pdfFileUrl: '',
            errors: new Errors(),
            searchQuery: '',
            perPage: 50,
            viewportHeight: window.innerHeight,
            summary: {
                total_revenues: '0',
                total_expenses: '0',
                net_profit:     '0',
                accounts_count: 0
            },
            totals: {
                amount: '0'
            },
            rows: [],
            meta: { current_page: 1, last_page: 1, total: null },
            datePresets: [
                { value: 'this_month', label: 'This Month' },
                { value: 'last_month', label: 'Last Month' },
                { value: 'custom',     label: 'Custom' }
            ],
            model: {
                branch_id:     [],
                currency_id:   null,
                date_preset:   'this_month',
                date_from:     null,
                date_to:       null,
                classified_by: 'summary',
                category:      null,
                sort:          'report_order_id',
                sort_dir:      'asc',
                page:          1,
                excel_template: 'standard'
            }
        }
    },
    computed: {
        ...mapState('report/resource', ['currencies']),
        isKh () { return (this.$i18n && this.$i18n.locale === 'kh') },
        isCustomDate () { return this.model.date_preset === 'custom' },
        presetPreview () {
            const today = moment()
            const fmt = d => d.format('DD MMM YYYY')
            switch (this.model.date_preset) {
                case 'this_month':
                    return fmt(today.clone().startOf('month')) + ' → ' + fmt(today.clone().endOf('month'))
                case 'last_month':
                    return fmt(today.clone().subtract(1, 'month').startOf('month'))
                        + ' → ' + fmt(today.clone().subtract(1, 'month').endOf('month'))
                default: return ''
            }
        },
        hasRows () { return (this.rows || []).length > 0 },
        tableHeight () {
            const chrome = this.filtersOpen ? 580 : 450
            const h = this.viewportHeight - chrome
            return Math.max(280, h)
        },
        emptyBodyHeight () {
            const PAGINATION_ROW = 52
            return this.tableHeight + PAGINATION_ROW
        },
        rangeStart () {
            if (!this.meta.total) return 0
            return (this.meta.current_page - 1) * this.perPage + 1
        },
        rangeEnd () {
            if (!this.meta.total) return 0
            return Math.min(this.meta.current_page * this.perPage, this.meta.total)
        },
        activeChips () {
            const chips = []
            let dateLabel = this.presetPreview
            if (this.model.date_preset === 'custom') {
                if (this.model.date_from && this.model.date_to) {
                    dateLabel = this.model.date_from + ' → ' + this.model.date_to
                } else {
                    dateLabel = 'Custom'
                }
            }
            chips.push({ key: 'date', icon: 'fa-calendar-day', label: dateLabel, tone: 'chip-date' })
            if (this.model.currency_id) {
                const c = (this.currencies || []).find(x => x.currency_id === this.model.currency_id)
                chips.push({ key: 'cur', icon: 'fa-coins', label: c ? c.currency_code : '', tone: 'chip-cur' })
            }
            if (this.model.classified_by && this.model.classified_by !== 'summary') {
                const map = { 'branch': 'By Branch', 'summary-by-branch': 'Summary by Branch' }
                chips.push({ key: 'mode', icon: 'fa-layer-group', label: map[this.model.classified_by], tone: 'chip-cat' })
            }
            if (this.model.category) {
                chips.push({ key: 'cat', icon: 'fa-tag',
                    label: this.model.category.charAt(0).toUpperCase() + this.model.category.slice(1),
                    tone: 'chip-cat' })
            }
            if ((this.model.branch_id || []).length) {
                chips.push({ key: 'branch', icon: 'fa-building',
                    label: this.model.branch_id.length + ' branch(es)', tone: 'chip-branch' })
            }
            const sq = (this.searchQuery || '').trim()
            if (sq) chips.push({ key: 'q', icon: 'fa-search', label: '“' + sq + '”', tone: 'chip-q' })
            return chips
        },
        summaryCards () {
            const s = this.summary
            const cur = (this.currencies || []).find(x => x.currency_id === this.model.currency_id)
            const code = cur ? cur.currency_code : ''
            const net = parseFloat(s.net_profit || 0)
            return [
                {
                    key: 'rev', tone: 'cur_paid', icon: 'fa-arrow-trend-up',
                    label: 'Total Revenues',
                    formatted: this.fmt(s.total_revenues),
                    unit: code
                },
                {
                    key: 'exp', tone: 'past_due', icon: 'fa-arrow-trend-down',
                    label: 'Total Expenses',
                    formatted: this.fmt(s.total_expenses),
                    unit: code
                },
                {
                    key: 'net',
                    tone: net >= 0 ? 'early' : 'past_due',
                    icon: 'fa-balance-scale',
                    label: 'Net Profit',
                    formatted: this.fmt(s.net_profit),
                    unit: code
                },
                {
                    key: 'acc', tone: 'cur_due', icon: 'fa-list-ol',
                    label: 'Accounts',
                    formatted: (s.accounts_count || 0).toLocaleString(),
                    badge: s.accounts_count || 0
                }
            ]
        },
        columns () {
            if (this.model.classified_by === 'summary-by-branch') {
                return [
                    { title: 'Branch Code', key: 'branch_code', width: 130, fixed: 'left', sortable: 'custom',
                      render: (h, p) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, p.row.branch_code || '—') },
                    { title: 'Branch Name', key: 'branch_name_en', minWidth: 220, sortable: 'custom',
                      render: (h, p) => h('span', { class: 'name-text' }, this.isKh
                          ? (p.row.branch_name_kh || p.row.branch_name_en || '—')
                          : (p.row.branch_name_en || p.row.branch_name_kh || '—')) },
                    { title: 'Total Revenues', key: 'total_revenues', width: 180, align: 'right', sortable: 'custom',
                      render: (h, p) => this.moneyCell(p.row.total_revenues) },
                    { title: 'Total Expenses', key: 'total_expenses', width: 180, align: 'right', sortable: 'custom',
                      render: (h, p) => this.moneyCell(p.row.total_expenses) },
                    { title: 'Total Profit', key: 'total_profit', width: 180, align: 'right', sortable: 'custom',
                      render: (h, p) => this.moneyCell(p.row.total_profit) }
                ]
            }

            const cols = []
            if (this.model.classified_by === 'branch') {
                cols.push({ title: 'Branch', key: 'branch_code', width: 120, fixed: 'left', sortable: 'custom',
                    render: (h, p) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, p.row.branch_code || '—') })
            }
            cols.push(
                { title: 'Order', key: 'report_order_id', width: 80, align: 'center', sortable: 'custom',
                  render: (h, p) => h('span', { class: 'tw-text-xs tw-text-gray-500' }, p.row.report_order_id || '—') },
                { title: 'Category', key: 'category', width: 130, fixed: this.model.classified_by === 'branch' ? false : 'left',
                  render: (h, p) => h('span', { class: 'pl-category pl-cat-' + String(p.row.category || '').toLowerCase() },
                      p.row.category || '—') },
                { title: 'Sub Category', key: 'sub_category', width: 170,
                  render: (h, p) => h('span', { class: 'tw-text-xs tw-text-gray-600' }, this.isKh
                      ? (p.row.sub_category_kh || p.row.sub_category || '—')
                      : (p.row.sub_category || '—')) },
                { title: 'Account #', key: 'account_number', width: 130, sortable: 'custom',
                  render: (h, p) => h('code', { class: 'tw-text-xs tw-text-gray-700' }, p.row.account_number || '—') },
                { title: 'Account Title', key: 'account_name_en', minWidth: 240, sortable: 'custom',
                  render: (h, p) => h('span', { class: 'name-text' }, this.isKh
                      ? (p.row.account_name_kh || p.row.account_name_en || '—')
                      : (p.row.account_name_en || p.row.account_name_kh || '—')) },
                { title: 'Amount', key: 'amount', width: 170, align: 'right', sortable: 'custom',
                  render: (h, p) => this.moneyCell(p.row.amount) }
            )
            return cols
        }
    },
    methods: {
        ...mapActions('report/resource', ['getCurrency']),

        fmt (v) {
            const n = parseFloat(v)
            if (isNaN(n) || n === 0) return '—'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        fmtStrict (v) {
            const n = parseFloat(v)
            if (isNaN(n)) return '0'
            return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
        },
        moneyCell (val) {
            const h = this.$createElement
            const n = parseFloat(val)
            if (isNaN(n) || n === 0) return h('span', { class: 'tw-text-gray-300' }, '—')
            return h('span', this.fmt(val))
        },

        onSearch () {
            if (!this.model.currency_id) {
                this.notice({ type: 'warning', text: 'Please pick a currency.' })
                return
            }
            if (this.isCustomDate && (!this.model.date_from || !this.model.date_to)) {
                this.notice({ type: 'warning', text: 'Please pick a custom date range.' })
                return
            }
            this.model.page = 1
            this.filtersOpen = false
            this.fetchData()
        },

        onClearSearch () {
            const had = !!(this.searchQuery || '').trim()
            this.searchQuery = ''
            if (had && this.meta.total !== null) {
                this.model.page = 1
                this.fetchData()
            }
        },

        onReset () {
            this.searchQuery = ''
            this.model.branch_id = []
            this.model.date_preset = 'this_month'
            this.model.date_from = null
            this.model.date_to = null
            this.model.classified_by = 'summary'
            this.model.category = null
            this.model.sort = 'report_order_id'
            this.model.sort_dir = 'asc'
            this.model.page = 1
            this.rows = []
            this.meta = { current_page: 1, last_page: 1, total: null }
        },

        onSortChange ({ key, order }) {
            if (order === 'normal') {
                const mode = this.model.classified_by
                this.model.sort = mode === 'summary' ? 'report_order_id'
                                : mode === 'summary-by-branch' ? 'branch_name_en'
                                : 'branch_code'
                this.model.sort_dir = 'asc'
            } else {
                this.model.sort = key
                this.model.sort_dir = order === 'asc' ? 'asc' : 'desc'
            }
            this.model.page = 1
            this.fetchData()
        },

        onPageChange (page) { this.model.page = page; this.fetchData() },
        onPerPageChange () { this.model.page = 1; this.fetchData() },

        buildParams () {
            const p = {
                currency_id:    this.model.currency_id,
                date_preset:    this.model.date_preset,
                classified_by:  this.model.classified_by,
                sort:           this.model.sort,
                sort_dir:       this.model.sort_dir,
                page:           this.model.page,
                perPage:        this.perPage,
                excel_template: this.model.excel_template
            }
            if ((this.model.branch_id || []).length) p.branch_id = this.model.branch_id
            if (this.model.date_preset === 'custom') {
                if (this.model.date_from) p.date_from = this.model.date_from
                if (this.model.date_to)   p.date_to   = this.model.date_to
            }
            if (this.model.category) p.category = this.model.category
            const sq = (this.searchQuery || '').trim()
            if (sq.length >= 2) p.search = sq
            return p
        },

        fetchData () {
            this.loading = true
            this.$store
                .dispatch('report/accounting/profitAndLossV3', this.buildParams())
                .then(res => {
                    this.rows = res.data || []
                    this.meta = {
                        current_page: res.meta ? res.meta.current_page : 1,
                        last_page:    res.meta ? res.meta.last_page    : 1,
                        total:        res.meta ? res.meta.total        : 0
                    }
                    if (res.summary) this.summary = res.summary
                    if (res.totals)  this.totals  = res.totals
                })
                .catch(err => {
                    const msg = (err && err.message) || 'Failed to load'
                    this.notice({ type: 'error', text: msg })
                    this.errors = new Errors(err && err.errors ? err.errors : {})
                })
                .finally(() => { this.loading = false })
        },

        exportExcel () {
            if (!this.model.currency_id) {
                this.notice({ type: 'warning', text: 'Please pick a currency first.' })
                return
            }
            if (this.isCustomDate && (!this.model.date_from || !this.model.date_to)) {
                this.notice({ type: 'warning', text: 'Please pick a custom date range.' })
                return
            }
            this.errors = new Errors()
            this.exporting = true
            this.$store
                .dispatch('report/accounting/profitAndLossV3ExportExcel', this.buildParams())
                .then(response => {
                    const filename = trim(
                        response.headers['content-disposition']
                            .substring(response.headers['content-disposition'].indexOf('filename'))
                            .replace('filename=', ''),
                        '"'
                    )
                    FileDownload(response.data, filename)
                })
                .catch(err => {
                    let msg = 'Export failed'
                    try {
                        const parsed = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(err)))
                        msg = parsed.message || msg
                        this.errors = new Errors(parsed.errors || {})
                    } catch (e) { /* not JSON */ }
                    this.notice({ type: 'error', text: msg })
                })
                .finally(() => { this.exporting = false })
        },

        summaryMethod ({ columns }) {
            const sums = {}
            const t = this.totals || {}
            columns.forEach((col, idx) => {
                if (idx === 0) { sums[col.key] = { key: col.key, value: 'TOTAL' }; return }
                let value = ''
                switch (col.key) {
                    case 'amount':         value = this.fmtStrict(t.amount);         break
                    case 'total_revenues': value = this.fmtStrict(t.total_revenues); break
                    case 'total_expenses': value = this.fmtStrict(t.total_expenses); break
                    case 'total_profit':   value = this.fmtStrict(t.total_profit);   break
                }
                sums[col.key] = { key: col.key, value }
            })
            return sums
        },

        printPdf () {
            if (!this.model.currency_id) {
                this.notice({ type: 'warning', text: 'Please pick a currency first.' })
                return
            }
            if (this.isCustomDate && (!this.model.date_from || !this.model.date_to)) {
                this.notice({ type: 'warning', text: 'Please pick a custom date range.' })
                return
            }
            this.errors = new Errors()
            this.pdfLoading = true
            this.pdfModalOpen = true
            this.pdfFileUrl = ''

            this.$store
                .dispatch('report/accounting/profitAndLossV3ExportPdf', this.buildParams())
                .then(response => {
                    this.pdfFileUrl = (response && response.url) || ''
                    if (!this.pdfFileUrl) {
                        this.notice({ type: 'error', text: 'PDF response was empty' })
                        this.pdfModalOpen = false
                    }
                })
                .catch(err => {
                    const msg = (err && err.message) || 'PDF failed'
                    this.notice({ type: 'error', text: msg })
                    this.pdfModalOpen = false
                })
                .finally(() => { this.pdfLoading = false })
        },

        onClosePdfModal () { this.pdfFileUrl = '' },

        notice (n) { this.$Notice[n.type]({ title: 'PROFIT AND LOSS', desc: n.text }) },
        onResize () { this.viewportHeight = window.innerHeight }
    },
    watch: {
        // When the user switches modes, reset sort to the mode's natural
        // default — a stale sort column from the previous mode may not
        // exist in the new mode's SELECT and would 500 the backend.
        'model.classified_by' (mode) {
            this.model.sort = mode === 'summary' ? 'report_order_id'
                            : mode === 'summary-by-branch' ? 'branch_name_en'
                            : 'branch_code'
            this.model.sort_dir = 'asc'
            this.model.page = 1
        }
    },
    created () {
        const sys = this.$store.state.authUser.sys_setting
        if (sys && sys[0] && sys[0].default_currency_id) {
            this.model.currency_id = sys[0].default_currency_id
        }
    },
    mounted () {
        this.getCurrency().then(() => {
            if (!this.model.currency_id && this.currencies.length) {
                this.model.currency_id = this.currencies[0].currency_id
            }
        })
        window.addEventListener('resize', this.onResize)
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.onResize)
    }
}
</script>

<style scoped>
.filter-grid {
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 10px;
    align-items: end;
}
@media (max-width: 991px) {
    .filter-grid > .filter-cell { grid-column: span 12 !important; }
}
.filter-cell { min-width: 0; }
.filter-lbl {
    display: block; font-size: 11px; font-weight: 600;
    text-transform: uppercase; letter-spacing: 0.03em;
    color: #6b7280; margin-bottom: 4px; height: 15px;
}
::v-deep .ivu-select-selection { min-height: 34px; }
::v-deep .ivu-input { height: 34px; }

.pill-group {
    display: inline-flex; align-items: center; flex-wrap: nowrap; gap: 3px;
    padding: 3px; background: #f3f4f6; border-radius: 6px;
    height: 34px; box-sizing: border-box; overflow-x: auto; max-width: 100%;
}
.pill {
    background: transparent; border: none; padding: 4px 10px;
    font-size: 12px; color: #4b5563; border-radius: 4px;
    cursor: pointer; transition: all 0.15s; line-height: 1.2;
    white-space: nowrap; display: inline-flex; align-items: center;
}
.pill:hover { background: #e5e7eb; }
.pill-active {
    background: #ffffff; color: #111827; font-weight: 600;
    box-shadow: 0 1px 2px rgba(0,0,0,0.08);
}
.date-preview {
    display: flex; align-items: center; padding: 0 12px; height: 34px;
    background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px;
    font-size: 13px; color: #374151; min-width: 180px;
}

.search-group {
    display: flex; align-items: stretch; height: 34px;
    border: 1px solid #d1d5db; border-radius: 6px;
    overflow: hidden; background: #ffffff;
}
.search-input-wrap { position: relative; flex: 1; display: flex; align-items: stretch; min-width: 0; }
.search-input {
    flex: 1; border: none !important; height: 100% !important;
    padding: 0 32px 0 12px; outline: none; background: transparent;
    font-size: 13px; color: #111827; min-width: 0;
}
.search-input:focus { box-shadow: none !important; }
.search-clear {
    position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
    background: transparent; border: none; padding: 4px;
    font-size: 14px; color: #9ca3af; cursor: pointer;
}
.search-btn {
    background: #3b82f6; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.search-btn:hover:not(:disabled) { background: #2563eb; }
.search-btn:disabled { background: #93c5fd; cursor: wait; }
.reset-btn {
    background: #f9fafb; color: #6b7280; border: none;
    border-left: 1px solid #e5e7eb; padding: 0 12px; cursor: pointer;
}
.reset-btn:hover { background: #f3f4f6; color: #374151; }

.chip-pill {
    display: inline-flex; align-items: center;
    padding: 2px 8px; font-size: 11px; font-weight: 600;
    border-radius: 11px; white-space: nowrap;
    background: #eef2ff; color: #4338ca;
}
.chip-default { background: #f3f4f6; color: #4b5563; }
.chip-date    { background: #ede9fe; color: #6d28d9; }
.chip-cur     { background: #ecfdf5; color: #047857; }
.chip-cat     { background: #fff7ed; color: #c2410c; }
.chip-branch  { background: #eff6ff; color: #1d4ed8; }
.chip-q       { background: #fef3c7; color: #92400e; }

.results-card {
    background: #ffffff; border-radius: 6px; overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}
.results-toolbar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 16px; border-bottom: 1px solid #e5e7eb;
    background: #ffffff; gap: 8px; flex-wrap: wrap;
}
.results-body { min-height: 120px; }
.results-pagination {
    display: flex; flex-wrap: wrap; justify-content: space-between;
    align-items: center; padding: 10px 16px; border-top: 1px solid #e5e7eb;
    background: #f9fafb; gap: 8px; min-height: 52px;
}
.pagination-left {
    display: flex; align-items: center; gap: 10px;
    height: 28px; line-height: 28px; font-size: 13px; color: #6b7280;
}
.pagination-lbl { white-space: nowrap; }
.pagination-lbl b { color: #111827; font-weight: 600; margin: 0 2px; }
.pagination-divider { color: #d1d5db; }

.export-group {
    display: inline-flex; align-items: stretch; height: 32px;
    border-radius: 5px; overflow: hidden;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    border: 1px solid #d1d5db; background: #ffffff;
}
.export-select {
    border: 0; border-right: 1px solid #d1d5db;
    background: #ffffff; padding: 0 28px 0 12px;
    font-size: 12px; color: #374151; font-weight: 500;
    cursor: pointer; outline: none; height: 100%;
    appearance: none; -webkit-appearance: none;
    background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3e%3cpath fill='%236b7280' d='M5 6L0 0h10z'/%3e%3c/svg%3e");
    background-repeat: no-repeat; background-position: right 10px center; background-size: 10px 6px;
}
.export-btn {
    background: #10b981; color: white; border: none;
    padding: 0 16px; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center;
}
.export-btn:hover:not(:disabled) { background: #059669; }
.export-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }

.pdf-btn {
    background: #ef4444; color: white; border: none;
    padding: 0 14px; font-size: 13px; font-weight: 600;
    height: 32px; border-radius: 5px;
    cursor: pointer; display: inline-flex; align-items: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.pdf-btn:hover:not(:disabled) { background: #dc2626; }
.pdf-btn:disabled { background: #fca5a5; cursor: not-allowed; }

::v-deep .pdf-modal .ivu-modal-body {
    padding: 0;
    height: calc(100vh - 51px);
    background: #525659;
}
.pdf-modal-body {
    position: relative; width: 100%; height: 100%; background: #525659;
}
.pdf-frame { width: 100%; height: 100%; display: block; }
.pdf-modal-action {
    background: #f9fafb; color: #374151; border: 1px solid #d1d5db;
    padding: 4px 12px; font-size: 12px; font-weight: 600; border-radius: 4px;
    cursor: pointer; transition: background 0.15s; text-decoration: none;
    display: inline-flex; align-items: center;
}
.pdf-modal-action:hover:not(:disabled) { background: #f3f4f6; }
.pdf-modal-action:disabled { color: #9ca3af; background: #f3f4f6; cursor: not-allowed; }

::v-deep .ivu-table th {
    background: #f9fafb !important;
    height: 40px; color: #374151; font-weight: 600;
    font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em;
}
::v-deep .ivu-table th .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
::v-deep .ivu-table-summary td {
    background: #f9fafb !important; font-weight: 700 !important;
    color: #111827 !important; border-top: 2px solid #d1d5db !important;
}
::v-deep .ivu-table td .ivu-table-cell {
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 4px 12px;
}
::v-deep .ivu-table td { height: 34px; }
::v-deep .ivu-table-small td { height: 32px; }

.empty-state {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px 20px; text-align: center; height: 100%; position: absolute; inset: 0;
}

.summary-grid {
    display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px;
}
@media (max-width: 991px) { .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 575px) { .summary-grid { grid-template-columns: 1fr; } }
.summary-card {
    display: flex; flex-direction: column; justify-content: space-between; gap: 8px;
    padding: 12px 14px; background: #ffffff;
    border: 1px solid #e5e7eb; border-top: 3px solid;
    border-radius: 6px; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    min-height: 86px;
}
.summary-card__header { display: flex; align-items: center; gap: 8px; }
.summary-card__icon {
    width: 22px; height: 22px; display: flex; align-items: center; justify-content: center;
    border-radius: 4px; font-size: 11px; flex-shrink: 0;
}
.summary-card__label {
    flex: 1; font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.03em; color: #6b7280; line-height: 1.2;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.summary-card__count {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    padding: 1px 6px; background: #f3f4f6; border-radius: 10px; line-height: 1.4;
}
.summary-card__value {
    display: flex; align-items: baseline; gap: 6px;
    line-height: 1; white-space: nowrap; overflow: hidden;
}
.summary-card__amount {
    font-size: 20px; font-weight: 700; color: #111827;
    overflow: hidden; text-overflow: ellipsis; min-width: 0;
}
.summary-card__currency {
    font-size: 11px; font-weight: 600; color: #9ca3af;
    letter-spacing: 0.03em; flex-shrink: 0;
}
.summary-card--cur_due  { border-top-color: #6366f1; }
.summary-card--cur_due  .summary-card__icon { background: #eef2ff; color: #4338ca; }
.summary-card--cur_paid { border-top-color: #10b981; }
.summary-card--cur_paid .summary-card__icon { background: #ecfdf5; color: #047857; }
.summary-card--early    { border-top-color: #3b82f6; }
.summary-card--early    .summary-card__icon { background: #eff6ff; color: #1e40af; }
.summary-card--past_due { border-top-color: #ef4444; }
.summary-card--past_due .summary-card__icon { background: #fee2e2; color: #991b1b; }
</style>

<!-- Unscoped: render-function elements don't receive data-v-xxx scoping. -->
<style>
.name-text {
    color: #111827; font-size: 12px; font-weight: 500;
}
.pl-category {
    display: inline-block; padding: 2px 8px;
    font-size: 11px; font-weight: 700; letter-spacing: 0.03em;
    border-radius: 10px;
}
.pl-cat-revenues { background: #ecfdf5; color: #047857; }
.pl-cat-expense  { background: #fee2e2; color: #991b1b; }
.pl-cat-expenses { background: #fee2e2; color: #991b1b; }
</style>
